var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { RoomChangedMessage, ModuleId, LayoutCreationFinishedMessage, DeleteModelOnDisconnectFacet, DestroyOnDisconnectFacet } from "flavr-common";
import { RoomTransformationUpdateMessage } from "@messages/scenegraph/ScenegraphMessages";
import { StartSessionMessage } from "@messages/session/SessionMessages";
import { modelPool, waitForModelWithPathAsync } from "@modules/model";
import { Module } from "@modules/module";
import { usersService } from "@modules/multiplayer/UsersService";
import { PeerManager } from "./PeerManager";
import { AvatarPeer } from "./AvatarPeer";
import { PeerRTCMessageHandler } from "./PeerRTCMessageHandler";
import { PeerImmediatelyMessageHandler } from "./PeerImmediatelyMessageHandler";
import { PeerModelMessageHandler } from "./PeerModelMessageHandler";
import { PeerControllerMessageHandler } from "./PeerControllerMessageHandler";
import { PeerSessionMessageHandler } from "./PeerSessionMessageHandler";
import { peerService } from "./PeerService";
import { serverConnection } from "../server/ServerConnectionService";
/**
 * Coordinates peer-specific state so other connected users, their avatars and their controllers stay synchronized with the current session.
 *
 * Facets:
 * - `DestroyOnDisconnectFacet` and `DeleteModelOnDisconnectFacet` - Describe which models should be cleaned up automatically when a peer disconnects.
 *
 * Messages:
 * - `StartSessionMessage` - Creates the local peer avatar for the new session.
 * - `RoomChangedMessage`, `RoomTransformationUpdateMessage` and `LayoutCreationFinishedMessage` - Keep peer avatars and controllers synchronized with the active room state.
 */
export class PeerModule extends Module {
    /**
     * Wires up the peer manager and all peer-related message/facet handlers (session start, room changes, layout
     * completion, and destroy/delete-on-disconnect facets), and instantiates the sub-handlers that manage RTC, session,
     * controller and model synchronization for peers.
     */
    constructor() {
        super(ModuleId.PEER);
        this.peerMgr = new PeerManager(this.connection);
        /**
         * Resolves the initial room model for the new session and creates, registers and sets the local user's own avatar
         * network object in it.
         */
        this.handleStartSessionMessage = (msg) => __awaiter(this, void 0, void 0, function* () {
            const roomModel = yield waitForModelWithPathAsync(msg.initialRoomId);
            if (roomModel === undefined) {
                console.warn("[multiplayer] Start session: no room id was found!");
                return;
            }
            this.peerMgr.onRoomChanged(roomModel.getUID());
            // create own avatar
            const avatar = this.peerMgr.createMyNetObject(AvatarPeer);
            avatar.currentRoomUID = roomModel.getUID();
            this.peerMgr.setMyAvatar();
            this.peerMgr.registerMyNetworkObject(avatar);
        });
        this.onConnectionToServerLost = () => {
            this.peerMgr.removeAllPeers();
        };
        this.handleRoomChangedMessage = (msg) => {
            if (!modelPool.hasModel(msg.uid))
                return;
            this.peerMgr.onRoomChanged(msg.uid);
        };
        this.handleRoomTransformationUpdateMessage = (msg) => {
            for (const avatar of this.peerMgr.peerAvatars.values()) {
                if (avatar.currentRoomUID === undefined)
                    continue;
                if (avatar.currentRoomUID !== msg.uid)
                    continue;
                avatar.onRoomTransformUpdate(msg.transform);
            }
            for (const controller of this.peerMgr.peersControllers.values()) {
                if (controller.currentRoomUID === undefined)
                    continue;
                if (controller.currentRoomUID !== msg.uid)
                    continue;
                controller.onRoomTransformUpdate(msg.transform);
            }
        };
        this.handleLayoutCreationFinishedMessage = (msg) => {
            for (const avatar of this.peerMgr.peerAvatars.values()) {
                if (avatar.currentRoomUID === undefined)
                    continue;
                if (avatar.currentRoomUID !== msg.uid)
                    continue;
                avatar.onLayouterCreationFinished(msg.uid);
            }
        };
        this.handleDestroyOnDisconnectFacet = (uid, facet) => {
            this.peerMgr.addDestroyModelOnDisconnect(facet.clientId, uid);
        };
        this.handleDeleteModelOnDisconnectFacet = (uid, facet) => {
            const user = Array.from(usersService.users.values()).find((it) => it.clientIdx === facet.clientIdx);
            if (user === undefined) {
                console.warn(`[multiplayer] No user found for DeleteModelOnDisconnectFacet clientIdx ${facet.clientIdx}`);
                return;
            }
            this.peerMgr.addDestroyModelOnDisconnect(user.clientId, uid);
        };
        this.handleRemoveDestroyOnDisconnectFacet = (uid) => {
            this.peerMgr.removeDestroyModelsOnDisconnectByUid(uid);
        };
        peerService.peerMgr = this.peerMgr;
        serverConnection.closeDelegate.add(this.onConnectionToServerLost);
        this.connection.subscribeByType(StartSessionMessage, this.handleStartSessionMessage);
        this.connection.subscribeByType(RoomChangedMessage, this.handleRoomChangedMessage);
        this.connection.subscribeByType(RoomTransformationUpdateMessage, this.handleRoomTransformationUpdateMessage);
        this.connection.subscribeByType(LayoutCreationFinishedMessage, this.handleLayoutCreationFinishedMessage);
        this.connection.subscribeByFacetType(DestroyOnDisconnectFacet, this.handleDestroyOnDisconnectFacet);
        this.connection.subscribeByRemoveFacetType(DestroyOnDisconnectFacet, this.handleRemoveDestroyOnDisconnectFacet);
        this.connection.subscribeByFacetType(DeleteModelOnDisconnectFacet, this.handleDeleteModelOnDisconnectFacet);
        this.connection.subscribeByRemoveFacetType(DeleteModelOnDisconnectFacet, this.handleRemoveDestroyOnDisconnectFacet);
        new PeerSessionMessageHandler(this.peerMgr);
        new PeerRTCMessageHandler(this.peerMgr);
        new PeerControllerMessageHandler(this.connection, this.peerMgr);
        new PeerImmediatelyMessageHandler(this.connection, this.peerMgr);
        new PeerModelMessageHandler(this.connection, this.peerMgr);
    }
    /**
     * Initializes the peer manager after all peer-related handlers and services have been wired up in the constructor.
     */
    init() {
        this.peerMgr.init();
    }
}
